"use client";

// CANONICAL SOURCE — flare/reporters/next/components/FlareClient.tsx
// Copied to each Next app as `components/FlareClient.tsx` and mounted ONCE in the
// root layout, inside <body>.

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { readIdentity, redactTokenPath } from "../lib/flare";

const MAX_BUFFERED = 20;
const FLUSH_MS = 5_000;
// A stored user id is re-checked at most this often on a page load, a return to
// the tab, or a route change; and, with none of those, every RECHECK_IDLE_MS on
// the flush tick, so an open tab nobody navigates still learns a sign-out.
const RECHECK_MS = 60_000;
const RECHECK_IDLE_MS = 15 * 60_000;

type Buffered = {
  kind: "client";
  name: string;
  message: string;
  stack: string | null;
  url: string | null;
  release: string | null;
  environment: "production";
  occurredAt: number;
  context: null;
  userRef: string | null;
  sessionId: string | null;
};

// Session cookies (no Max-Age/Expires): they die with the browser session, and
// they exist only so the server half (instrumentation.ts) can read the same ids.
const COOKIE_ATTRS = "Path=/; SameSite=Lax; Secure";
const setCookie = (name: string, value: string) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; ${COOKIE_ATTRS}`;
};
const clearCookie = (name: string) => {
  document.cookie = `${name}=; ${COOKIE_ATTRS}; Max-Age=0`;
};

/**
 * `identity="next-auth"` opts an Auth.js app into per-person tracing: after the
 * page goes idle, this asks `/api/auth/session` for the signed-in user's id and
 * keeps it as `flare_uid`. Without the prop only the per-tab session id is sent.
 * The id, never the email: ingest drops any value with `@`.
 */
// Module state lives exactly as long as the page load: a client-side navigation
// or a remount keeps it, a reload resets it.
let lastLookupAt: number | null = null;

export default function FlareClient({ identity }: { identity?: "next-auth" }) {
  // The fleet's Sign out is a server action whose redirect is a CLIENT-SIDE
  // navigation: this component never remounts, so a route change is the one
  // signal that a sign-out may just have happened. (No app sets
  // cacheComponents; under it, usePathname in the root layout can need Suspense.)
  const pathname = usePathname();
  const recheckUser = useRef<(() => void) | null>(null);

  useEffect(() => {
    const endpoint = process.env.NEXT_PUBLIC_FLARE_URL;
    const key = process.env.NEXT_PUBLIC_FLARE_KEY;
    if (!endpoint || !key || process.env.NEXT_PUBLIC_VERCEL_ENV !== "production") return;

    const release = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ?? null;
    let buffer: Buffered[] = [];
    let dropped = 0;

    // ONE SESSION ID PER TAB. sessionStorage is per tab and survives reloads, so
    // the id ties together every error in one sitting. Blocked storage (Safari
    // private mode throws here) means no id, never a thrown reporter.
    try {
      if (!sessionStorage.getItem("flare_sid")) sessionStorage.setItem("flare_sid", crypto.randomUUID());
    } catch {
      // Report without a session id.
    }

    // THE COOKIE IS SHARED BY EVERY TAB; sessionStorage is not. Whichever tab
    // wrote it last wins, so each tab re-writes its own ids whenever it becomes
    // the one in front: the server errors a person can trigger come from the tab
    // they are using. Written only from what storage holds, so the two halves
    // never disagree.
    const mirrorToCookies = () => {
      try {
        const sid = sessionStorage.getItem("flare_sid");
        if (sid) setCookie("flare_sid", sid);
        // A tab holding no id clears whatever another tab left there.
        const uid = sessionStorage.getItem("flare_uid");
        if (uid) setCookie("flare_uid", uid);
        else clearCookie("flare_uid");
      } catch {
        // Blocked storage or cookies: nothing to mirror.
      }
    };
    mirrorToCookies();

    // A STORED ID IS A CACHE, NOT A FACT. It is re-checked on every page load,
    // and again when the tab comes back to the front or the route changes,
    // once RECHECK_MS has passed since the last lookup (Auth.js's own
    // SessionProvider refetches on load and focus too), and on the flush tick
    // once RECHECK_IDLE_MS has. That is what catches a sign-out, an account
    // switch, or a sign-out in another tab.
    //
    // A signed-out reply forgets the id, in storage and cookie, but is not itself
    // remembered beyond the page load, so a sign-in by OAuth redirect, which
    // reloads the tab, is learned at once. Auth.js v5 answers signed-out with the
    // JSON body `null` (@auth/core's session action), older versions with `{}`;
    // both are "signed out", never "not Auth.js". Only an endpoint that is plainly
    // not Auth.js (a 404, a body that is not JSON, or JSON that is not an object
    // or null) is remembered for the tab and never asked again. A 5xx, any other
    // non-2xx, or a network failure is transient: whatever is stored is kept.
    let idleHandle: number | null = null;
    let idleIsTimeout = false;
    let busy = false;
    const lookUpUser = async () => {
      idleHandle = null;
      lastLookupAt = Date.now();
      let notAuthJs = false;
      let signedOut = false;
      try {
        const res = await fetch("/api/auth/session", { credentials: "same-origin" });
        if (res.status === 404) {
          notAuthJs = true;
        } else if (res.ok) {
          let json: unknown;
          try {
            json = await res.json();
          } catch {
            notAuthJs = true;
          }
          if (!notAuthJs) {
            if (json === null) {
              signedOut = true;
            } else if (typeof json !== "object" || Array.isArray(json)) {
              notAuthJs = true;
            } else {
              const id = (json as { user?: { id?: unknown } }).user?.id;
              const ref = typeof id === "string" && id ? id : typeof id === "number" && Number.isFinite(id) ? String(id) : null;
              if (ref) {
                sessionStorage.setItem("flare_uid", ref);
                setCookie("flare_uid", ref);
                return;
              }
              signedOut = true;
            }
          }
        }
      } catch {
        // A network failure is transient.
      } finally {
        busy = false;
      }
      if (!notAuthJs && !signedOut) return;
      try {
        if (notAuthJs) sessionStorage.setItem("flare_uid_checked", "1");
        sessionStorage.removeItem("flare_uid");
        clearCookie("flare_uid");
      } catch {
        // Nothing to record or clear.
      }
    };
    const maybeLookUpUser = (minAge = RECHECK_MS) => {
      if (identity !== "next-auth" || busy) return;
      if (lastLookupAt !== null && Date.now() - lastLookupAt < minAge) return;
      try {
        // Reading storage first also proves it usable: if it cannot be read, a
        // found id could not be kept either, so no lookup is made at all.
        if (sessionStorage.getItem("flare_uid_checked")) return;
        busy = true;
        if (typeof window.requestIdleCallback === "function") {
          idleIsTimeout = false;
          idleHandle = window.requestIdleCallback(() => void lookUpUser(), { timeout: 5_000 });
        } else {
          idleIsTimeout = true;
          idleHandle = window.setTimeout(() => void lookUpUser(), 2_000);
        }
      } catch {
        // Blocked storage: no lookup.
      }
    };
    maybeLookUpUser();
    recheckUser.current = () => maybeLookUpUser();

    const push = (name: string, message: string, stack: string | null) => {
      // A render loop can throw thousands of times per second. Past the cap we
      // stop buffering but keep counting, so the flush can say how many were lost
      // rather than silently under-reporting.
      if (buffer.length >= MAX_BUFFERED) {
        dropped += 1;
        return;
      }
      buffer.push({
        kind: "client",
        name: name || "Error",
        message: message || "Unknown error",
        stack,
        // No query string: the server half sends request.path with no query
        // too, so the two halves agree, and a query string can carry a real
        // identifier in some apps (vitals, tide) — not part of the
        // fingerprint, so dropping it costs nothing but exposure. A caller
        // that needs a query value passes it explicitly via context. A bearer
        // token in the path (/invite/<token>) is a credential: redacted.
        url: redactTokenPath(window.location.pathname),
        release,
        environment: "production",
        occurredAt: Date.now(),
        context: null,
        // Read at push time: the user lookup may land after the page loads.
        ...readIdentity(),
      });
    };

    const flush = (useBeacon: boolean) => {
      if (buffer.length === 0) return;
      const events = buffer;
      const lost = dropped;
      buffer = [];
      dropped = 0;
      if (lost > 0) {
        events.push({
          kind: "client",
          name: "FlareBufferOverflow",
          message: `${lost} further client errors on this page load were not buffered`,
          stack: null,
          url: redactTokenPath(window.location.pathname),
          release,
          environment: "production",
          occurredAt: Date.now(),
          context: null,
          ...readIdentity(),
        });
      }
      const body = JSON.stringify({ events });
      try {
        // sendBeacon survives the page being torn down; fetch does not. It cannot
        // set a custom header, so the key rides as a query parameter on the
        // beacon path only. It is an ingest key, not a user credential.
        if (useBeacon && typeof navigator.sendBeacon === "function") {
          navigator.sendBeacon(`${endpoint}?k=${encodeURIComponent(key)}`, new Blob([body], { type: "application/json" }));
          return;
        }
        void fetch(endpoint, {
          method: "POST",
          headers: { "content-type": "application/json", "x-flare-key": key },
          body,
          signal: AbortSignal.timeout(2_000),
          keepalive: true,
        }).catch(() => {});
      } catch {
        // Never let reporting break the page.
      }
    };

    // `window.onerror` still names the file, line and column when `e.error` is
    // null — a throw the browser could not hand us an Error for. Synthesising a
    // single V8-shaped frame from them is the difference between a group with a
    // real culprit and an anonymous "Error" with nothing to act on; flare's
    // culpritFrames parses this exact shape.
    //
    // The one throw that gives neither an Error nor a filename is a CROSS-ORIGIN
    // one: the browser replaces the message with the literal "Script error." and
    // blanks filename/lineno/colno, deliberately, so a page cannot read another
    // origin's internals. That combination is unfixable by anyone reading it, and
    // flare's ingest drops it on exactly that signature — which is only safe
    // BECAUSE this handler now populates a frame in every case that is not it.
    const frameFrom = (e: ErrorEvent) =>
      e.filename ? `    at ${e.filename}:${e.lineno ?? 0}:${e.colno ?? 0}` : null;
    const onError = (e: ErrorEvent) =>
      push(e.error?.name ?? "Error", e.message, e.error?.stack ?? frameFrom(e));
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      push(r?.name ?? "UnhandledRejection", r?.message ?? String(r), r?.stack ?? null);
    };
    const onFront = () => {
      mirrorToCookies();
      maybeLookUpUser();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush(true);
      else onFront();
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onFront);
    const timer = window.setInterval(() => {
      flush(false);
      maybeLookUpUser(RECHECK_IDLE_MS);
    }, FLUSH_MS);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onFront);
      recheckUser.current = null;
      window.clearInterval(timer);
      if (idleHandle !== null) {
        if (idleIsTimeout) window.clearTimeout(idleHandle);
        else window.cancelIdleCallback?.(idleHandle);
      }
      flush(true);
    };
  }, [identity]);

  useEffect(() => {
    recheckUser.current?.();
  }, [pathname]);

  return null;
}
